/**
 * haul_captures + haul_capture_tanks + driver_check_ins (DB-06).
 *
 * The CAPTURE side of the dispatch graph. Phases 6 (run-ticket capture),
 * 8 (geofencing), and 11 (truck pusher) read/write these tables. Schema
 * is derived from master spec §13 (HAUL CAPTURE — one job, many tank
 * captures), §14 (HAUL ID — 9-digit format), §15 (GEOFENCING event_type
 * vocabulary), §16 (CHECK-IN CHAIN — sync semantics), and §32 (audit
 * event types).
 *
 * Quick 260517-d8h item 2 (2026-05-17): per-tank seal_number column added to
 * haul_capture_tanks. Extends DEC-003's seal_required job_type flag to a
 * per-tank seal capture. Parent haul_captures.seal_off_number / seal_on_number
 * remain for unit-level seals (DEC-003 unchanged); the new column is
 * additive.
 *
 * DEC-023 (2026-05-16): Multi-tank capture. haul_captures (parent) now
 * owns N child rows in haul_capture_tanks. Per-tank gauge + color_cut +
 * free-text tank_name moved to the child; signature + photos stay on the
 * parent; total BBL is computed on read as
 * SUM(GREATEST(COALESCE(after_bbl,0)-COALESCE(before_bbl,0),0)).
 * The four parent columns bbl_before / bbl_after / bbl_net / color_cut
 * are dropped by migration 0006 and are no longer in this Drizzle
 * declaration. haul_capture_tanks has no tenant_id / pgPolicy — isolation
 * flows transitively through the FK to haul_captures.
 *
 * Tables (3 total — haul_captures + haul_capture_tanks + driver_check_ins):
 *   - haul_captures: per-capture row inside a job (RLS-enforced, tenant-
 *     scoped). Master spec §13 ONE-TO-MANY enforced via UNIQUE
 *     (job_id, capture_sequence). Surrogate UUID PK for clean FK targets.
 *   - haul_capture_tanks: N child rows per haul_captures parent (DEC-023).
 *     No tenant_id — isolation transitive via FK (see DEC-023).
 *   - driver_check_ins: master spec §16 check-in chain. Append-only at the
 *     application layer (Phase 6 / Phase 8 enforce no-UPDATE/no-DELETE).
 *     check_in_type is locked to EXACTLY 4 values verbatim from master
 *     spec §15 GpsTrailPoint event_type (docs/CLAUDE.md lines 1310-1316)
 *     and §32 audit event types (line 2901): ARRIVED_PICKUP /
 *     DEPARTED_PICKUP / ARRIVED_DISPOSAL / DEPARTED_DISPOSAL.
 *
 * Composite-key approach for haul_captures (MASTER SPEC §13 ONE-TO-MANY):
 *   - Surrogate `id` UUID PK (universal FK target — billing, audit, etc.)
 *   - UNIQUE index on (job_id, capture_sequence) enforces the §13 invariant
 *   - tenant_id + RLS still apply
 *
 *   Why surrogate + UNIQUE rather than `primaryKey({ columns: [jobId, captureSequence] })`?
 *   Because audit_events (Plan 02-04) and billable_events (Plan 02-04 / Phase 12)
 *   need a stable single-column FK target; a composite-PK FK ripples through
 *   every consumer. The UNIQUE constraint provides the same data-integrity
 *   guarantee at the cost of one extra index. Surrogate-key + composite
 *   UNIQUE is the standard pattern across this schema.
 *
 * check_in_type allowlist (LOCKED — 4 values from master spec §15):
 *   ARRIVED_PICKUP / DEPARTED_PICKUP / ARRIVED_DISPOSAL / DEPARTED_DISPOSAL
 *
 *   The §15 vocabulary uses DEPARTED_* (not LEFT_*) and DISPOSAL (not
 *   DROP_OFF) — align driver_check_ins to the spec vocabulary so cross-
 *   references with audit_events and gps_trail_points (Phase 8) are
 *   consistent. GEOFENCE_ENTER / GEOFENCE_EXIT are mechanism-level events
 *   emitted as audit events (§32) — they trigger the ARRIVED_* / DEPARTED_*
 *   check-ins but are not check_in_type values themselves. The mechanism
 *   flag (GEOFENCE_AUTO vs MANUAL) lives in audit_events payload (Phase 5+)
 *   per master spec §32 line 2901: 'ARRIVED_PICKUP (GEOFENCE_AUTO or MANUAL
 *   tagged in payload)'. Adding a 5th check_in_type value requires DEC-NNN.
 *
 * Append-only contract (driver_check_ins): Phase 6 + Phase 8 enforce no-
 * UPDATE/no-DELETE at the app layer. The synced_at column records when the
 * server received the event — for sync-ordering analysis and the chain-
 * validation trace (master spec §16). Phase 23 may add a Postgres trigger
 * raising on UPDATE/DELETE for app_user as defense-in-depth.
 *
 * RLS posture (CONTEXT D-15): both tables tenant-scoped with the standard
 *   `tenant_id = current_setting('app.current_tenant_id')::uuid`
 * predicate. 2 pgPolicy calls total: haul_captures, driver_check_ins.
 *
 * OPEN-001 deferral: NO `color_cut` column on haul_captures. Master spec
 * §13 has no color-cut column; OPEN-001 documents a placeholder UI list
 * configurable in Phase 6 once real-world hauler input arrives. If Phase 6
 * decides a stored color-cut value is needed, that plan adds the column
 * via a new migration. Phase 2 leaves zero references.
 *
 * Sources:
 *   - master spec §13 (HAUL CAPTURE — one job, many tank captures; gauge
 *     fields: top + bottom in feet+inches × before + after = 8 columns)
 *   - master spec §14 (HAUL ID — 9-digit digits-only format; CHECK regex
 *     `^[0-9]{9}$` matches jobs.haul_id constraint)
 *   - master spec §15 (GEOFENCING — GpsTrailPoint event_type vocabulary;
 *     docs/CLAUDE.md lines 1310-1316)
 *   - master spec §16 (CHECK-IN CHAIN — sync semantics; previous_check_in_id
 *     self-FK forms the chain)
 *   - master spec §32 (AUDIT TRAIL — line 2901 audit event types include
 *     'ARRIVED_PICKUP (GEOFENCE_AUTO or MANUAL tagged in payload)')
 *   - DEC-003 (seal_off_number + seal_on_number nullable on HaulCapture)
 *   - CONTEXT D-15, D-16 (RLS + role split)
 *   - CONTEXT D-18 / OPEN-001 (color-cut deferral to Phase 6)
 *   - RESEARCH Performance Indexes #17-20 (haul_captures: 3; driver_check_ins: 1)
 *
 * Locked contracts (future phases must NOT change without DEC-NNN):
 *   - haul_captures.haul_id is varchar(20) via CHECK regex
 *     `^[A-Z]{2,6}[0-9]{9}$` (master spec §14 + Phase 6 / Plan 06-01 /
 *     D-22 step 4; same regex as jobs.haul_id). Widened from legacy
 *     varchar(9) digits-only via migration 0005.
 *   - haul_captures has UNIQUE (job_id, capture_sequence) — master spec §13
 *     one-to-many invariant.
 *   - haul_captures.capture_sequence has CHECK (>= 1).
 *   - haul_captures.volume_source has CHECK in
 *     ('CALCULATED','DIRECT_ENTRY','MANUAL_OVERRIDE') OR NULL
 *     (Phase 6 / Plan 06-01 / D-22 step 6, master spec §13 semantics).
 *   - haul_captures.photo_s3_keys is jsonb (widened from text in
 *     migration 0005) to support the `||` array-append pattern in
 *     `routes/jobs/uploads/photo-complete.ts`.
 *   - haul_captures.completion_location SNAPSHOT from parent job_types
 *     (DEC-029 / D-17). Same snapshot semantics as capture_method: DEFAULT is
 *     migration safety only; API sets explicitly at INSERT; OMITTED from
 *     ON CONFLICT DO UPDATE SET.
 *   - haul_captures.other_destination_text — OTHER-branch driver-entered destination.
 *     CHECK invariant: OTHER ⇔ text present.
 *   - haul_capture_tanks child rows are ordered by position ASC (0-based
 *     array index from mobile client). Replace-all semantics on update:
 *     DELETE + re-INSERT in one transaction (DEC-023).
 *   - haul_capture_tanks has NO tenant_id / pgPolicy — tenant isolation is
 *     transitive through the FK to haul_captures (DEC-023). The API MUST
 *     NOT query haul_capture_tanks without joining haul_captures.
 *   - driver_check_ins.check_in_type CHECK allowlist is exactly 4 values
 *     verbatim from master spec §15 / §32.
 *   - driver_check_ins is append-only — Phase 6+ application code MUST NOT
 *     UPDATE or DELETE rows; only INSERT.
 * Phase 06.2 additions (DEC-031 #3 + DEC-030 #2/#3):
 *   - driver_total_time_minutes INTEGER NULL — HOURLY billing time entry,
 *     REQUIRED-when-set enforced at disposal-complete endpoint, not DB.
 *   - driver_comments TEXT NULL — free-text driver note (OS-keyboard
 *     exception to CLAUDE.md §4 numeric-only rule); whitespace-only
 *     normalized to NULL at Zod layer (Plan 06.2-15).
 *   - haul_capture_tanks.product_id is a nullable FK to products(id) with
 *     ON DELETE RESTRICT. When the API receives a capture with productId
 *     null/absent on a tank, it resolves the value to the job_type's
 *     default_product_id at INSERT time (snapshot semantics — mirrors
 *     DEC-024's capture_method snapshot). The column must NEVER be re-
 *     resolved on read; historical captures are immutable per master spec
 *     §32 audit-trail principle.
 */

import {
  boolean,
  check,
  index,
  integer,
  jsonb,
  numeric,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { jobs } from './jobs.js';
import { products } from './products.js';
// D-12 / JOBTYPE-08 — the registered-tank link (hauler_tanks is the live
// registration table; site_tanks was retired by DEC-147 / Phase 26.2).
import { haulerTanks } from './platform-catalog.js';

/**
 * haul_captures — per-tank-capture row inside a job (master spec §13).
 *
 * Surrogate UUID PK + UNIQUE (job_id, capture_sequence) — see module
 * docstring for the trade-off rationale. master spec §14 9-digit haul_id
 * format CHECK matches jobs.haul_id regex (two-table consistency).
 *
 * 3 perf indexes (RESEARCH #17, #18, #19) plus the UNIQUE (job_id,
 * capture_sequence) data-integrity index:
 *   - (job_id, capture_sequence) UNIQUE — §13 one-to-many invariant
 *   - (tenant_id, job_id) — composite-key lookup (RESEARCH #17)
 *   - (tenant_id, created_at DESC) — recent-captures dashboard (#18)
 *   - (tenant_id, tank_id, created_at DESC) — per-tank gauging history
 *     for Phase 13 TANK-01 fill-rate calc (#19)
 *
 * Master spec §13 gauge fields are stored as feet (integer) + inches
 * (numeric(4,2)) for both TOP and BOTTOM, BEFORE and AFTER (8 columns
 * total). The mobile app's FeetInchesPicker (CLAUDE.md root spec)
 * captures these directly — no fractional-feet rounding.
 *
 * DEC-003 nullable seal columns: seal_off_number + seal_on_number. Phase 6
 * decides whether to populate based on jobTypes.sealRequired (DEC-003).
 *
 * Signature + photo S3 keys: Phase 6 wires the S3 upload path; Phase 2
 * declares the columns. signature_s3_key is a single key (one signature
 * per capture); photo_s3_keys is a JSON array of keys (multiple photos
 * per capture).
 *
 * captured_by_user_id references users.id (NOT drivers.id) per the §10
 * identity model — the capturing user is identified by their user row.
 */
export const haulCaptures = pgTable(
  'haul_captures',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id')
      .notNull()
      .references(() => jobs.id, { onDelete: 'restrict' }),
    // (haul_captures.tank_id → site_tanks RETIRED — Phase 26.2 dropped the old model.)
    // 1, 2, 3, ... per job (master spec §13).
    captureSequence: integer('capture_sequence').notNull(),
    // Master spec §14 + Phase 6 / Plan 06-01 / D-22 step 4 — server-issued
    // canonical HaulId. Widened from varchar(9) (legacy 9-digit) to varchar(20)
    // to fit the new `{2-6-letter abbreviation}{9 digit sequence}` format
    // (e.g. `RIV000000042`, 3+9=12 chars; 20 leaves headroom for the maximum
    // 6+9=15 char form). Format CHECK below enforces the regex.
    haulId: varchar('haul_id', { length: 20 }).notNull(),
    // Volume measurement columns (bbl_before, bbl_after, bbl_net) REMOVED by
    // migration 0006 (DEC-023) — they moved to haul_capture_tanks child rows.
    // Total BBL is now computed on read via
    // SUM(GREATEST(COALESCE(after_bbl,0)-COALESCE(before_bbl,0),0)).
    // Master spec §13 gauge fields — TOP × {BEFORE, AFTER}.
    gaugeTopBeforeFeet: integer('gauge_top_before_feet'),
    gaugeTopBeforeInches: numeric('gauge_top_before_inches', { precision: 4, scale: 2 }),
    gaugeTopAfterFeet: integer('gauge_top_after_feet'),
    gaugeTopAfterInches: numeric('gauge_top_after_inches', { precision: 4, scale: 2 }),
    // Master spec §13 gauge fields — BOTTOM × {BEFORE, AFTER}.
    gaugeBottomBeforeFeet: integer('gauge_bottom_before_feet'),
    gaugeBottomBeforeInches: numeric('gauge_bottom_before_inches', { precision: 4, scale: 2 }),
    gaugeBottomAfterFeet: integer('gauge_bottom_after_feet'),
    gaugeBottomAfterInches: numeric('gauge_bottom_after_inches', { precision: 4, scale: 2 }),
    // Seal numbers moved to per-tank (migration 0076 / DEC-170 seal redesign) —
    // haul_capture_tanks.seal_off_number / seal_on_number. No job-level seal.
    // color_cut REMOVED by migration 0006 (DEC-023) — moved to
    // haul_capture_tanks child rows (per-tank color cut, not per-capture).
    // OPEN-001 placeholder list is unchanged — same values apply per-tank.
    // Phase 6 / Plan 06-01 / D-22 step 6 — master spec §13 volume-source
    // semantics. CHECK constraint below restricts to the three §13 values.
    //   CALCULATED      — derived from gauge feet+inches via tank geometry
    //   DIRECT_ENTRY    — driver typed BBL into BBLInput directly
    //   MANUAL_OVERRIDE — driver overrode a calculated value
    volumeSource: text('volume_source'),
    // DEC-024 — capture_method SNAPSHOT from job_types.capture_method at
    // insert time. NOT defaulted by the API (the column DEFAULT is migration
    // safety only). Once written, this value is the audit/billing record of
    // which capture shape was used — changing the job_type's method later
    // does NOT retroactively rewrite this column.
    // NULLABLE (Phase 12.1, migration 0076): LINE_ITEM captures have no tank-gauge
    // shape, so they store capture_method NULL (not the vestigial SINGLE_GAUGE
    // default). TANK captures keep their real SINGLE_GAUGE/BEFORE_AFTER value.
    captureMethod: text('capture_method').default('SINGLE_GAUGE'),
    // D-17 — completion_location SNAPSHOT from job_types.completion_location at
    // insert time. Same snapshot semantics as DEC-024 capture_method: NOT
    // defaulted by the API (the column DEFAULT is migration safety only). Once
    // written, this is the audit record of where the job actually completed —
    // changing the job_type's completion_location later does NOT retroactively
    // rewrite this column.
    completionLocation: text('completion_location').notNull().default('DISPOSAL_FACILITY'),
    // D-17 OTHER branch — driver-entered destination when completion_location='OTHER'.
    // CHECK constraint below encodes the invariant: OTHER ⇔ text present.
    otherDestinationText: text('other_destination_text'),
    // Capture metadata.
    capturedAt: timestamp('captured_at', { withTimezone: true }).notNull(),
    capturedByUserId: uuid('captured_by_user_id').references(() => users.id, {
      onDelete: 'restrict',
    }),
    capturedGpsLat: numeric('captured_gps_lat', { precision: 10, scale: 7 }),
    capturedGpsLng: numeric('captured_gps_lng', { precision: 10, scale: 7 }),
    capturedGpsAccuracyFt: integer('captured_gps_accuracy_ft'),
    // ClickUp 86bbrwg66 (migration 0082) — CAPTURE-TIME EQUIPMENT SNAPSHOT.
    // Spec §10's "what gets stamped on all jobs this shift", copied from the
    // driver's shifts.active_truck_id / active_trailer_id at capture time.
    //
    // NOT per-job assignment (a dispatcher choosing equipment for a job) — that is
    // out of scope. This is an audit snapshot, the same pattern as the existing
    // job_events.facility_id disposal-facility snapshot (D-09).
    //
    // Needed for CORRECTNESS: a run ticket is REPRINTED days later
    // (buildRunTicketFromCompleted). Resolving the truck live from the shift at
    // print time would stamp every earlier ticket with whatever truck the driver is
    // on NOW — wrong after any mid-shift swap, and a swap usually means a breakdown,
    // exactly when the paperwork matters. There is also no job_id -> shift_id link
    // in the schema to re-derive it from afterwards.
    //
    // NULL on every pre-0082 row and never backfilled — no client had ever recorded
    // a truck, so inferring one would be inventing evidence on a compliance record.
    // Soft uuid columns (no inline .references): hauls.ts does not import trucks or
    // trailers. FKs owned by migration 0082, relations wired in relations.ts.
    truckId: uuid('truck_id'),
    trailerId: uuid('trailer_id'),
    // Phase 6 fills these in via S3 upload path; column declared here.
    signatureS3Key: text('signature_s3_key'),
    // Phase 6 / Plan 06-01 (Group C complement) — JSONB array of S3 keys
    // (multiple photos per capture). Widened from text -> jsonb in migration
    // 0005 to enable the JSONB `||` append pattern in
    // `routes/jobs/uploads/photo-complete.ts` (Plan 06-08).
    photoS3Keys: jsonb('photo_s3_keys'),
    // Phase 06.2 (DEC-031 #3, DEC-030 #2) — HOURLY billing time entry.
    // NULL for non-HOURLY job_types. REQUIRED-when-set enforcement at the
    // disposal-complete endpoint (Plan 06.2-15), NOT at DB level.
    driverTotalTimeMinutes: integer('driver_total_time_minutes'),
    // Phase 06.2 (DEC-031 #3, DEC-030 #3) — free-text driver note.
    // OS-keyboard entry permitted (exception to CLAUDE.md §4 numeric-only
    // rule; comments are not numeric). Whitespace-only normalized to NULL
    // at Zod layer (Plan 06.2-15). No length CHECK at DB level.
    driverComments: text('driver_comments'),
    // OPEN-121 / migration 0242 — what the driver's app reported at capture. photos_claimed is
    // the number of photos the driver took (NULL = an older build or a non-driver writer);
    // photo_skip_reason is the reason for skipping a REQUIRED photo (trimmed, >= 10 chars).
    // MISSING_PHOTO is computed from these, never from photo_s3_keys (photos upload later).
    photosClaimed: integer('photos_claimed'),
    photoSkipReason: text('photo_skip_reason'),
    // OPEN-217 / DEC-382 (migration 0266) — the rule warnings the driver confirmed, each with a
    // snapshot of the rule as shown (ValidationConfirmation[] in shared validation-rules.ts).
    // NULL = written by a build that predates rules; [] = evaluated, nothing confirmed.
    validationConfirmations: jsonb('validation_confirmations'),
    // Phase 11 / Task 11.4 (DEC-125) — truck-pusher volume review override. NULL
    // reviewed_net_bbl = no edit (use the computed net). When set, the captured
    // tank values are NEVER destroyed — the original computed net is preserved and
    // shown side-by-side. reviewed_net_bbl_reason (≥10 chars, enforced at the API)
    // is mandatory when reviewed_net_bbl is set. reviewed_by/at record the actor.
    reviewedNetBbl: numeric('reviewed_net_bbl', { precision: 10, scale: 2 }),
    reviewedNetBblReason: text('reviewed_net_bbl_reason'),
    reviewedBy: uuid('reviewed_by').references(() => users.id, { onDelete: 'set null' }),
    reviewedAt: timestamp('reviewed_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Master spec §13 ONE-TO-MANY invariant — UNIQUE (job_id, capture_sequence).
    uniqueIndex('haul_captures_job_sequence_idx').on(t.jobId, t.captureSequence),
    // DEC-247 / migration 0115 — haul_id is the operator public API's PUBLIC handle
    // for a ticket (GET /v1/operator-api/tickets/{haulId}), so it is guaranteed by a
    // constraint rather than by the `ORDER BY capture_sequence, id LIMIT 1` tie-break
    // 0097/0098 pinned inside operator_portal_ticket_detail. It is also what makes
    // (captured_at, haul_id) a TOTAL order, which is what the API's keyset cursor
    // pages over. Safe to add: haul_id is copied verbatim from jobs.haul_id, which
    // already carries the global unique jobs_haul_id_idx.
    uniqueIndex('haul_captures_haul_id_idx').on(t.haulId),
    // RESEARCH #17 — composite-key lookup (tenant_id + job_id).
    index('haul_captures_tenant_job_idx').on(t.tenantId, t.jobId),
    // RESEARCH #18 — recent-captures dashboard (DASH-01).
    index('haul_captures_tenant_created_idx').on(t.tenantId, t.createdAt.desc()),
    // ClickUp 86bbrwg66 (migration 0082) — equipment-snapshot FK indexes
    // ("every ticket hauled by truck X" for compliance / maintenance lookups).
    index('haul_captures_truck_idx').on(t.truckId),
    index('haul_captures_trailer_idx').on(t.trailerId),
    // Master spec §14 + Phase 6 / D-22 step 4 + Phase 24 / Task 24.4 (DEC-095) —
    // accept legacy `{2-6 letters}{9 digits}` OR new `{2-4}-{2-4}{9 digits}`
    // (matches jobs.haul_id; two-table consistency). NOT NULL via column.
    check(
      'haul_captures_haul_id_format_check',
      sql`haul_id ~ '^[A-Z]{2,6}[0-9]{9}$' OR haul_id ~ '^[A-Z]{2,4}-[A-Z]{2,4}[0-9]{9}$'`,
    ),
    // capture_sequence is 1-indexed (master spec §13).
    check('haul_captures_sequence_positive_check', sql`capture_sequence >= 1`),
    // Phase 6 / D-22 step 6 — master spec §13 volume_source allowlist.
    check(
      'haul_captures_volume_source_check',
      sql`volume_source IS NULL OR volume_source IN ('CALCULATED', 'DIRECT_ENTRY', 'MANUAL_OVERRIDE')`,
    ),
    // DEC-024 — capture_method allowlist.
    check(
      'haul_captures_capture_method_check',
      sql`capture_method IN ('SINGLE_GAUGE', 'BEFORE_AFTER')`,
    ),
    // D-17 — completion_location allowlist.
    check(
      'haul_captures_completion_location_check',
      sql`completion_location IN ('DISPOSAL_FACILITY', 'ON_SITE', 'OTHER')`,
    ),
    // D-17 OTHER-branch invariant: OTHER ⇔ text present; DISPOSAL_FACILITY | ON_SITE ⇒ text absent.
    check(
      'haul_captures_other_destination_text_check',
      sql`(completion_location = 'OTHER' AND other_destination_text IS NOT NULL)
          OR (completion_location <> 'OTHER' AND other_destination_text IS NULL)`,
    ),
    check('haul_captures_photos_claimed_check', sql`photos_claimed IS NULL OR photos_claimed >= 0`),
    check(
      'haul_captures_photo_skip_reason_check',
      sql`photo_skip_reason IS NULL OR length(btrim(photo_skip_reason)) >= 10`,
    ),
    pgPolicy('haul_captures_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulCapture = typeof haulCaptures.$inferSelect;
export type NewHaulCapture = typeof haulCaptures.$inferInsert;
export const haulCaptureSelectSchema = createSelectSchema(haulCaptures);
export const haulCaptureInsertSchema = createInsertSchema(haulCaptures);

/**
 * haul_capture_tanks — N child rows per haul_capture (DEC-023).
 *
 * Tank identity resolution (D-11 / JOBTYPE-08, Phase 09.1): the server
 * always writes `tank_name` via a 3-step resolution order — (1) when
 * `hauler_tank_id` links a registered tank, auto-populate from that row's
 * `name`; (2) otherwise, if the job type's `tank_name_requirement` enum is
 * Optional/Required, use driver input; (3) if Hidden, or Optional and
 * skipped, fall back to the positional default (`Tank {position + 1}`).
 * `tank_name` stays NOT NULL throughout. Ordered by `position` ascending
 * (array index from the mobile client; 0-based).
 *
 * RLS: NO tenant_id column, NO pgPolicy. Isolation flows transitively
 * through the FK to haul_captures (which IS RLS-scoped). The API never
 * queries this table without joining haul_captures.
 *
 * Photos + signature stay on the parent. Color cut moves here (per-tank
 * per OPEN-001 — different tanks at the same wellsite can show different
 * color cuts).
 *
 * Replace-all update semantics: DELETE + re-INSERT inside one transaction
 * per DEC-023 locked decision #3. The API MUST NOT do partial child-row
 * updates — the entire child set is replaced on every capture write.
 */
export const haulCaptureTanks = pgTable(
  'haul_capture_tanks',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    haulCaptureId: uuid('haul_capture_id')
      .notNull()
      .references(() => haulCaptures.id, { onDelete: 'cascade' }),
    tankName: text('tank_name').notNull(),
    beforeBbl: numeric('before_bbl', { precision: 10, scale: 2 }),
    afterBbl: numeric('after_bbl', { precision: 10, scale: 2 }),
    colorCut: text('color_cut'),
    // Migration 0076 (DEC-170 seal redesign) — per-tank Seal Off # / Seal On #
    // pair. REPLACES the single per-tank seal_number (0010) AND the job-level
    // haul_captures.seal_off_number / seal_on_number (DEC-003); seal tracking is
    // per-tank only now. Nullable; required-ness is policy-driven by the
    // job_type's seal_requirement enum (DEC-170) — the columns accept NULL
    // freely, the API handler enforces non-null Off AND On per tank when the
    // enum = REQUIRED (mirrors DEC-025's flag-driven pattern).
    sealOffNumber: text('seal_off_number'),
    sealOnNumber: text('seal_on_number'),
    // D-12 / JOBTYPE-08 — the registered-tank link. site_tanks is RETIRED
    // (DEC-147 / Phase 26.2); the live registration table is hauler_tanks.
    // Nullable: an ad-hoc tank on a ticket has no registry row. When set, the
    // server resolves tank_name from hauler_tanks.name (D-11 resolution step 1).
    // ON DELETE SET NULL — a de-registered tank must not erase capture history.
    haulerTankId: uuid('hauler_tank_id').references(() => haulerTanks.id, {
      onDelete: 'set null',
    }),
    // DEC-026 (2026-05-17, quick 260517-d8h) — per-tank product reference.
    // Nullable: when null at write time, the capture handler INSERTs the
    // job_type's default_product_id as a snapshot (so historical rows stay
    // stable if the job_type default is later changed). ON DELETE RESTRICT
    // because captures are the audit-of-record for billing + compliance — a
    // product referenced by historical captures must NOT vanish. Soft-delete
    // via products.is_active = false instead.
    productId: uuid('product_id').references(() => products.id, { onDelete: 'restrict' }),
    // DEC-024 — SINGLE_GAUGE per-tank fields. All nullable. Populated only
    // when the parent capture_method is SINGLE_GAUGE; rejected at the Zod
    // boundary when capture_method is BEFORE_AFTER. Gauge feet/inches are
    // RAW audit data — no strapping-chart math derives volumes from them.
    // Driver enters water_volume_bbl + oil_volume_bbl separately.
    topGaugeFeet: integer('top_gauge_feet'),
    topGaugeInches: integer('top_gauge_inches'),
    bottomGaugeFeet: integer('bottom_gauge_feet'),
    bottomGaugeInches: integer('bottom_gauge_inches'),
    waterVolumeBbl: numeric('water_volume_bbl', { precision: 10, scale: 2 }),
    oilVolumeBbl: numeric('oil_volume_bbl', { precision: 10, scale: 2 }),
    position: integer('position').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('haul_capture_tanks_capture_position_idx').on(t.haulCaptureId, t.position),
    index('haul_capture_tanks_hauler_tank_idx').on(t.haulerTankId),
    check('haul_capture_tanks_position_nonneg_check', sql`position >= 0`),
  ],
);

export type HaulCaptureTank = typeof haulCaptureTanks.$inferSelect;
export type NewHaulCaptureTank = typeof haulCaptureTanks.$inferInsert;
export const haulCaptureTankSelectSchema = createSelectSchema(haulCaptureTanks);
export const haulCaptureTankInsertSchema = createInsertSchema(haulCaptureTanks);

/**
 * haul_capture_line_items — Phase 12.1 / D-02. A LINE_ITEM job's delivered products.
 *
 * Structural template: haulCaptureTanks (child of haulCaptures, cascade delete, no
 * own tenant_id/pgPolicy — isolation transitive via the FK, DELETE + bulk-INSERT
 * replace-all semantics on every capture write, D-02).
 *
 * Snapshot-semantics template: haulCaptureServiceCharges (DEC-030) —
 * unit_of_measure + rate_cents_at_capture are resolved server-side from the
 * products catalog at INSERT and are NEVER client-supplied, NEVER re-resolved on
 * read (D-05). product_id is NOT NULL (unlike haulCaptureTanks.productId) — a
 * line item's product IS the point of the row; there is no job-type default to
 * fall back to for a LINE_ITEM job type (D-04).
 */
export const haulCaptureLineItems = pgTable(
  'haul_capture_line_items',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    haulCaptureId: uuid('haul_capture_id')
      .notNull()
      .references(() => haulCaptures.id, { onDelete: 'cascade' }),
    productId: uuid('product_id')
      .notNull()
      .references(() => products.id, { onDelete: 'restrict' }),
    quantity: numeric('quantity', { precision: 10, scale: 3 }).notNull(),
    // Snapshot from products.unit_of_measure at INSERT (DEC-024/DEC-030 pattern). Immutable.
    unitOfMeasure: text('unit_of_measure').notNull(),
    // Snapshot from products.default_unit_rate_cents at INSERT. Immutable. Never
    // client-supplied. NULLABLE (Phase 12.1, migration 0076): an unpriced product
    // (products.default_unit_rate_cents IS NULL) snapshots NULL here — the capture
    // SUCCEEDS and the downstream billable_event carries rate NULL as the
    // "needs pricing" signal. The rate_nonneg CHECK (>= 0) still holds because a
    // CHECK passes when its expression is NULL.
    rateCentsAtCapture: integer('rate_cents_at_capture'),
    position: integer('position').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('haul_capture_line_items_capture_position_idx').on(t.haulCaptureId, t.position),
    check('haul_capture_line_items_qty_positive_check', sql`quantity > 0`),
    check('haul_capture_line_items_rate_nonneg_check', sql`rate_cents_at_capture >= 0`),
    check('haul_capture_line_items_position_nonneg_check', sql`position >= 0`),
  ],
);

export type HaulCaptureLineItem = typeof haulCaptureLineItems.$inferSelect;
export type NewHaulCaptureLineItem = typeof haulCaptureLineItems.$inferInsert;
export const haulCaptureLineItemSelectSchema = createSelectSchema(haulCaptureLineItems);
export const haulCaptureLineItemInsertSchema = createInsertSchema(haulCaptureLineItems);

/**
 * DRIVER_CHECK_IN_TYPES — exactly 4 values pinned verbatim from master spec
 * §15 GpsTrailPoint event_type list (docs/CLAUDE.md lines 1310-1316) and
 * §32 audit event types (line 2901):
 *
 *     check_in_type IN ('ARRIVED_PICKUP', 'DEPARTED_PICKUP',
 *                       'ARRIVED_DISPOSAL', 'DEPARTED_DISPOSAL')
 *
 * NO additional values. GEOFENCE_ENTER / GEOFENCE_EXIT are mechanism-level
 * audit events (§32), not check_in_type values. The mechanism flag
 * (GEOFENCE_AUTO vs MANUAL) lives in audit_events payload per §32 line 2901.
 *
 * Stored as `text` with a CHECK constraint at the column level (NOT pgEnum)
 * for the same evolvability rationale as enums.ts TENANT_USER_ROLES — text+
 * CHECK rewrites are routine; pgEnum ALTER is fragile (RESEARCH Open
 * Question #1 / drizzle issue #4295). Adding a 5th value requires DEC-NNN.
 */
export const DRIVER_CHECK_IN_TYPES = [
  'ARRIVED_PICKUP',
  'DEPARTED_PICKUP',
  'ARRIVED_DISPOSAL',
  'DEPARTED_DISPOSAL',
] as const;
export type DriverCheckInType = (typeof DRIVER_CHECK_IN_TYPES)[number];

/**
 * driver_check_ins — master spec §16 check-in chain.
 *
 * Append-only at the application layer (Phase 6 + Phase 8 enforce). RLS-
 * enforced. previous_check_in_id is a self-FK pattern — chain-head check-in
 * has NULL; subsequent check-ins point to the most recent prior one for
 * the same job (chain validation in Phase 8 GEO-04).
 *
 * 1 perf index (RESEARCH #20):
 *   - (tenant_id, job_id, captured_at) — check-in chain per job
 *
 * captured_at is the device-local time (driver clock); synced_at is the
 * server-receive timestamp (sync-ordering analysis per master spec §16).
 *
 * GPS columns: gps_lat / gps_lng / gps_accuracy_ft. The sync receiver
 * (Phase 6 / Phase 11) rejects (0, 0) coordinates per the no-silent-skip
 * rule from CLAUDE.md and SYNC-04.
 *
 * online_flag: TRUE = device was online when event happened; FALSE =
 * offline-captured + deferred sync. device_id captures the originating
 * device for audit purposes.
 */
export const driverCheckIns = pgTable(
  'driver_check_ins',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id')
      .notNull()
      .references(() => jobs.id, { onDelete: 'cascade' }),
    driverId: uuid('driver_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    // CHECK constraint defined below in the (t) => [...] callback.
    checkInType: text('check_in_type').notNull(),
    // Device-local time of the check-in event.
    capturedAt: timestamp('captured_at', { withTimezone: true }).notNull(),
    // Server-receive timestamp (sync-ordering analysis per §16).
    syncedAt: timestamp('synced_at', { withTimezone: true }).notNull().defaultNow(),
    gpsLat: numeric('gps_lat', { precision: 10, scale: 7 }),
    gpsLng: numeric('gps_lng', { precision: 10, scale: 7 }),
    gpsAccuracyFt: integer('gps_accuracy_ft'),
    // TRUE = device was online when event happened (sync wave 1); FALSE = offline.
    onlineFlag: boolean('online_flag'),
    deviceId: uuid('device_id'),
    // Self-FK chain pattern — chain head has NULL; subsequent check-ins
    // point to the most recent prior one for the same job. Phase 8 GEO-04
    // validates the chain.
    previousCheckInId: uuid('previous_check_in_id'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // RESEARCH #20 — check-in chain per job (timeline lookup).
    index('driver_check_ins_tenant_job_captured_idx').on(t.tenantId, t.jobId, t.capturedAt),
    // Master spec §15 / §32 — exactly 4 check_in_type values verbatim.
    check(
      'driver_check_ins_type_check',
      sql`check_in_type IN ('ARRIVED_PICKUP', 'DEPARTED_PICKUP', 'ARRIVED_DISPOSAL', 'DEPARTED_DISPOSAL')`,
    ),
    pgPolicy('driver_check_ins_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type DriverCheckIn = typeof driverCheckIns.$inferSelect;
export type NewDriverCheckIn = typeof driverCheckIns.$inferInsert;
export const driverCheckInSelectSchema = createSelectSchema(driverCheckIns);
export const driverCheckInInsertSchema = createInsertSchema(driverCheckIns);
